import { useState } from "react";
import { motion } from "framer-motion";
import { FileText, CheckCircle, Send } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const services = [
  "Reputación Corporativa",
  "Gestión de Crisis 360°",
  "Relaciones Públicas & Media Training",
  "Producción Audiovisual",
  "Paquete Integral",
  "Otro",
];

const budgets = [
  "Menos de $3,000",
  "$3,000 — $10,000",
  "$10,000 — $25,000",
  "Más de $25,000",
  "Prefiero discutirlo",
];

const timelines = [
  "Urgente (esta semana)",
  "Este mes",
  "Próximos 3 meses",
  "Estoy planificando",
];

export const QuoteSection = () => {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "",
    service: "", budget: "", timeline: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await axios.post(`${API}/api/quote`, form);
      setSubmitted(true);
    } catch (err) {
      setError("Error al enviar. Intenta de nuevo o contáctanos por WhatsApp.");
    }
    setSubmitting(false);
  };

  return (
    <section id="cotizar" className="relative py-32" data-testid="quote-section">
      <div className="morph-bg w-[400px] h-[400px] bg-purple-700/6 top-[10%] left-[-8%]" />

      <div className="relative z-10 max-w-4xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-14"
        >
          <div className="flex items-center justify-center gap-3 mb-6">
            <span className="pulse-dot" />
            <span className="text-[13px] text-white/35 font-space tracking-widest uppercase">Cotización</span>
          </div>
          <h2 className="font-space font-bold text-4xl md:text-6xl text-white mb-4 leading-tight">
            Solicita tu<br />propuesta
          </h2>
          <p className="text-white/40 text-lg max-w-lg mx-auto">
            Cuéntanos qué necesitas. Te respondemos en menos de 24 horas con una propuesta personalizada.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="glass-card p-8 md:p-12"
        >
          {submitted ? (
            <div className="text-center py-12" data-testid="quote-success">
              <div className="w-16 h-16 rounded-2xl bg-purple-500/8 border border-purple-500/15 backdrop-blur-md flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-purple-400/60" />
              </div>
              <h3 className="font-space font-semibold text-2xl text-white mb-2">Solicitud recibida</h3>
              <p className="text-white/40">Nuestro equipo revisará tu solicitud y te enviaremos una propuesta personalizada a tu correo.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-8" data-testid="quote-form">
              {/* Contact info */}
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-[13px] text-white/40 mb-2 font-space">Nombre *</label>
                  <input type="text" required value={form.name} onChange={(e) => set("name", e.target.value)} className="input" placeholder="Tu nombre completo" data-testid="quote-name" />
                </div>
                <div>
                  <label className="block text-[13px] text-white/40 mb-2 font-space">Email *</label>
                  <input type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} className="input" placeholder="tu@email.com" data-testid="quote-email" />
                </div>
                <div>
                  <label className="block text-[13px] text-white/40 mb-2 font-space">Teléfono</label>
                  <input type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="input" placeholder="+503..." data-testid="quote-phone" />
                </div>
                <div>
                  <label className="block text-[13px] text-white/40 mb-2 font-space">Empresa</label>
                  <input type="text" value={form.company} onChange={(e) => set("company", e.target.value)} className="input" placeholder="Tu empresa u organización" data-testid="quote-company" />
                </div>
              </div>

              {/* Service */}
              <div>
                <label className="block text-[13px] text-white/40 mb-3 font-space">Servicio de interés *</label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="quote-services">
                  {services.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => set("service", s)}
                      className={`px-4 py-3 rounded-xl border font-space text-sm text-left transition-all ${
                        form.service === s
                          ? "border-purple-500/35 bg-purple-500/8 text-white"
                          : "border-white/[0.06] bg-white/[0.02] text-white/40 hover:border-white/[0.12]"
                      }`}
                      data-testid={`quote-service-${s.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              {/* Budget */}
              <div>
                <label className="block text-[13px] text-white/40 mb-3 font-space">Presupuesto estimado</label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="quote-budgets">
                  {budgets.map((b) => (
                    <button
                      key={b}
                      type="button"
                      onClick={() => set("budget", b)}
                      className={`px-4 py-3 rounded-xl border font-space text-sm text-left transition-all ${
                        form.budget === b
                          ? "border-purple-500/35 bg-purple-500/8 text-white"
                          : "border-white/[0.06] bg-white/[0.02] text-white/40 hover:border-white/[0.12]"
                      }`}
                    >
                      {b}
                    </button>
                  ))}
                </div>
              </div>

              {/* Timeline */}
              <div>
                <label className="block text-[13px] text-white/40 mb-3 font-space">¿Cuándo lo necesitas?</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="quote-timelines">
                  {timelines.map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => set("timeline", t)}
                      className={`px-4 py-3 rounded-xl border font-space text-sm text-center transition-all ${
                        form.timeline === t
                          ? "border-purple-500/35 bg-purple-500/8 text-white"
                          : "border-white/[0.06] bg-white/[0.02] text-white/40 hover:border-white/[0.12]"
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="block text-[13px] text-white/40 mb-2 font-space">Cuéntanos más sobre tu proyecto</label>
                <textarea rows={4} value={form.message} onChange={(e) => set("message", e.target.value)} className="input resize-none" placeholder="Describe brevemente qué necesitas..." data-testid="quote-message" />
              </div>

              {error && <p className="text-red-400/80 text-sm">{error}</p>}

              <button
                type="submit"
                disabled={submitting || !form.service}
                className="btn-primary w-full py-4 disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid="quote-submit"
              >
                {submitting ? "Enviando..." : "Solicitar cotización"}
                <Send className="w-5 h-5" />
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};
